import { storage } from '../firebase/config';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

/**
 * Upload an image file to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} folder - Folder path in storage (e.g., 'products')
 * @param {Function} onProgress - Callback function for upload progress (0-100)
 * @returns {Promise<string>} - The download URL of the uploaded image
 */
export const uploadImage = async (file, folder = 'products', onProgress = null) => {
  if (!file) {
    throw new Error('No file provided');
  }

  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  // Validate file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB in bytes
  if (file.size > maxSize) {
    throw new Error('Image size must be less than 5MB');
  }

  // Generate unique filename
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 15);
  const fileExtension = file.name.split('.').pop();
  const fileName = `${timestamp}_${randomString}.${fileExtension}`;
  const storageRef = ref(storage, `${folder}/${fileName}`);

  // Create upload task with metadata
  const uploadTask = uploadBytesResumable(storageRef, file, {
    contentType: file.type,
  });

  return new Promise((resolve, reject) => {
    // Set up error handler first
    const errorHandler = (error) => {
      console.error('Upload error:', error);
      let errorMessage = 'Failed to upload image';
      
      // Provide more specific error messages
      if (error.code === 'storage/unauthorized') {
        errorMessage = 'Unauthorized: Please make sure you are logged in as admin';
      } else if (error.code === 'storage/canceled') {
        errorMessage = 'Upload was canceled';
      } else if (error.code === 'storage/unknown') {
        errorMessage = 'Unknown error occurred. Please check your connection and try again';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      reject(new Error(errorMessage));
    };

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        // Track upload progress
        if (snapshot.totalBytes > 0) {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(Math.min(progress, 100));
          }
        } else {
          // If totalBytes is 0, set progress to 0
          if (onProgress) {
            onProgress(0);
          }
        }
      },
      errorHandler,
      async () => {
        // Upload completed successfully
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          if (onProgress) {
            onProgress(100);
          }
          resolve(downloadURL);
        } catch (error) {
          console.error('Error getting download URL:', error);
          reject(new Error('Failed to get image URL: ' + (error.message || 'Unknown error')));
        }
      }
    );
  });
};

/**
 * Upload multiple images
 * @param {File[]} files - Array of image files
 * @param {string} folder - Folder path in storage
 * @param {Function} onProgress - Callback function for overall progress
 * @returns {Promise<string[]>} - Array of download URLs
 */
export const uploadMultipleImages = async (files, folder = 'products', onProgress = null) => {
  const uploadPromises = files.map((file, index) => {
    return uploadImage(file, folder, (progress) => {
      if (onProgress) {
        // Calculate overall progress
        const overallProgress = ((index / files.length) * 100) + (progress / files.length);
        onProgress(overallProgress);
      }
    });
  });

  return Promise.all(uploadPromises);
};

